export interface Range {
  min?: number;
  max?: number;
}

export type ParamValue = boolean | string | number | Range;
export type Values = Record<string, ParamValue>;

/**
 * When a filter (or one option of a filter) applies, based on another
 * filter's value. All listed conditions must hold.
 */
export interface Condition {
  /** Id of the other param. */
  param: string;
  /** Passes when the other param's value is one of these (choice values). */
  in?: string[];
  /** With `in`: also passes when the other param isn't set. */
  orUnset?: boolean;
  /** Passes when the other param isn't set. */
  unset?: true;
}

export interface Applicability {
  when?: Condition | Condition[];
  /** Why it's limited, for errors and docs: "only for video results". */
  whenLabel?: string;
}

interface BaseParam extends Applicability {
  id: string;
  label: string;
  /** Groups related params in the builder form. */
  group?: string;
  help?: string;
}

/** On/off filter written as a bare token: `!eb.fs` */
export interface FlagParam extends BaseParam {
  type: 'flag';
  token: string;
  aliases?: string[];
}

export interface ChoiceOption extends Applicability {
  value: string;
  token: string;
  label: string;
  aliases?: string[];
}

/** One of several options, each with its own token: `!yt.short` */
export interface ChoiceParam extends BaseParam {
  type: 'choice';
  options: ChoiceOption[];
  /**
   * Also offer a free-text value written into the query as `operator:value`
   * (e.g. `site:example.com`), for values that can't be bang tokens.
   */
  operator?: QueryOperator;
}

/**
 * A search operator typed into the query words (`intitle:word`, `-word`)
 * rather than the bang, because its value can hold dots or spaces. `kind`
 * decides how the value is checked and written.
 */
export interface QueryOperator {
  /** Operator name without the colon; `-` for excluded words. */
  name: string;
  kind: 'site' | 'filetype' | 'words' | 'date' | 'exclude';
  label: string;
  placeholder: string;
  help?: string;
}

/** Numeric range behind a prefix: `$50-200`, `$-200`, `$50-`, `$200` (max). */
export interface RangeParam extends BaseParam {
  type: 'range';
  prefix: string;
  unit?: string;
}

/** Single number with a prefix and/or suffix: `50mi`, `*100`. */
export interface NumberParam extends BaseParam {
  type: 'number';
  prefix?: string;
  suffix?: string;
  placeholder?: string;
}

/** Free text behind a prefix, constrained by a pattern: `@seattle`. */
export interface TextParam extends BaseParam {
  type: 'text';
  prefix: string;
  pattern: RegExp;
  placeholder?: string;
  /** Setting used when this is left empty, e.g. the Craigslist region. */
  setting?: SettingKey;
}

export type PeriodUnit = 'h' | 'd' | 'w' | 'm' | 'y';

/** Time window: `d`, `w`, `m6`, `y2`. Value is the token itself. */
export interface PeriodParam extends BaseParam {
  type: 'period';
  units: PeriodUnit[];
  /** Whether a count may follow the unit (`m6`). */
  counted: boolean;
}

/**
 * Date or date range, e.g. check-in to check-out: `10/17-10/19`, `10/17`,
 * `wknd` (next weekend), `tmrw`, `+7` (today plus 7 days). Value is the token;
 * engines resolve it with `resolveDates` at build time so relative dates stay
 * relative in presets.
 */
export interface DatesParam extends BaseParam {
  type: 'dates';
}

export type Param =
  | FlagParam
  | ChoiceParam
  | RangeParam
  | NumberParam
  | TextParam
  | PeriodParam
  | DatesParam;

export interface Settings {
  /** Bang (with optional modifiers) used when a query has none. */
  defaultBang: string;
  zip?: string;
  /** Default search radius in miles for engines that support one. */
  distance?: number;
  craigslistSite?: string;
  marketplaceCity?: string;
  /** Home coordinates as "lat,lng", for engines that search by location. */
  home?: string;
  ddgFallback: boolean;
  /** Keep a short list of recent searches in this browser. */
  keepRecent: boolean;
  /**
   * Modifiers applied whenever an engine's own bang is used, by engine id:
   * `{ ebay: 'us.fs' }`. Typed modifiers override them; `.plain` skips them.
   */
  engineDefaults?: Record<string, string>;
  /** Your own extra bangs for engines, by engine id: `{ ebay: ['ebay'] }`. */
  engineAliases?: Record<string, string[]>;
  /**
   * Affiliate tags on shopping searches (eBay, Amazon). Absent means on;
   * only `false` turns them off.
   */
  shoppingLinks?: boolean;
}

export type SettingKey = 'zip' | 'craigslistSite' | 'marketplaceCity' | 'home';

export type Category =
  | 'Web'
  | 'Video'
  | 'Shopping'
  | 'Local'
  | 'Events'
  | 'Travel'
  | 'Outdoors'
  | 'Jobs'
  | 'Developer'
  | 'Research'
  | 'Reference'
  | 'Media'
  | 'AI'
  | 'Custom';

export interface BuildInput {
  query: string;
  values: Values;
  settings: Settings;
}

export interface Engine {
  id: string;
  name: string;
  /** Primary bang, without `!`. */
  bang: string;
  aliases: string[];
  homepage: string;
  description: string;
  /** URL params here are partly reverse-engineered and may drift. */
  experimental?: boolean;
  category: Category;
  /** Shown in the Search Builder unless switched off on the Engines page. */
  defaultOn?: boolean;
  /** Some filters need you signed in on the site; shown as a note. */
  signIn?: string;
  /** A query (and modifiers) the engine needs to build, for tests and examples. */
  example?: { query: string; mods: string[] };
  params: Param[];
  /** Operators the engine understands in the query words, shown as fields. */
  operators?: QueryOperator[];
  build(input: BuildInput): string;
}

export interface Preset {
  /** Bang name for the preset, without `!`. */
  name: string;
  engine: string;
  values: Values;
  /** Words added to every search, e.g. `language:rust stars:>100`. */
  terms?: string;
  description?: string;
  /** Other bangs that run it too (`ap4s` for `ap4`), without `!`. */
  aliases?: string[];
}

/**
 * One bang that runs a search on several sites: `!shop iphone 15` opens eBay,
 * Mercari and Craigslist. Members are bangs without `!`, with modifiers.
 */
export interface Group {
  name: string;
  members: string[];
  description?: string;
}

/** Thrown by an engine's build when a required setting is missing. */
export class MissingSettingError extends Error {
  constructor(
    readonly setting: SettingKey,
    readonly engine: string
  ) {
    super(`${engine} needs the "${setting}" setting`);
  }
}

/** Thrown by an engine's build when the query itself is missing something,
 * e.g. flight searches need two airports and a date. */
export class NeedsInputError extends Error {
  constructor(
    message: string,
    readonly engine: string
  ) {
    super(message);
  }
}
